import { Injectable, signal, inject, NgZone, Injector } from '@angular/core';
import { ContextService } from './context.service';
import { ToastService } from './toast.service';
import { AuditService } from './audit.service';
import { supabase } from '../supabase';

/**
 * Serviço de Segurança de Sessão e Prevenção do FilaSaúde
 * Especialista: security-auditor
 * 
 * Responsabilidades:
 * 1. Monitoramento de Inatividade (15 min) com aviso regressivo (60s)
 * 2. Logout automático ao fechar abas/término de trabalho
 * 3. Sincronização multi-aba via BroadcastChannel
 * 4. Proteção contra ataques de força bruta no login (limite de 5 tentativas + bloqueio 5 min)
 * 5. Registro de trilha de auditoria para eventos de segurança
 */
@Injectable({
    providedIn: 'root'
})
export class SessionSecurityService {
    private injector = inject(Injector);
    private ngZone = inject(NgZone);

    private get context(): ContextService {
        return this.injector.get(ContextService);
    }

    private get toast(): ToastService {
        return this.injector.get(ToastService);
    }

    private get audit(): AuditService {
        return this.injector.get(AuditService);
    }

    // Constantes de Inatividade
    readonly INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutos
    readonly WARNING_BEFORE_MS = 60 * 1000;         // 60 segundos antes de encerrar
    readonly WARNING_THRESHOLD_MS = this.INACTIVITY_LIMIT_MS - this.WARNING_BEFORE_MS; // 14 minutos

    // Constantes de Força Bruta
    readonly MAX_FAILED_ATTEMPTS = 5;
    readonly LOCKOUT_DURATION_MS = 5 * 60 * 1000; // 5 minutos de bloqueio

    // Signals Reativos de Estado
    isWarningModalVisible = signal(false);
    remainingSeconds = signal(60);
    isLockedOut = signal(false);
    lockoutRemainingSeconds = signal(0);
    failedAttempts = signal(0);

    // Estado interno
    private lastActivityTime = Date.now();
    private checkIntervalId: any = null;
    private lockoutIntervalId: any = null;
    private broadcastChannel: BroadcastChannel | null = null;
    private tabId = 'tab_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now();
    private activityThrottleMs = 5000; // Throttle de 5s para não sobrecarregar
    private lastActivityRecorded = 0;
    private isMonitoring = false;

    constructor() {
        this.initMultiTabChannel();
        this.initTabLifecycle();
        this.checkInitialLockout();
    }

    /**
     * Inicia o monitoramento de inatividade quando o usuário faz login
     */
    startInactivityMonitoring() {
        if (this.isMonitoring) return;
        this.isMonitoring = true;
        this.lastActivityTime = Date.now();
        this.updateSharedActivityTime();

        this.attachActivityListeners();

        // Intervalo de verificação a cada 1 segundo (fora da zona se necessário para performance)
        this.ngZone.runOutsideAngular(() => {
            if (this.checkIntervalId) clearInterval(this.checkIntervalId);
            this.checkIntervalId = setInterval(() => {
                this.checkInactivityStatus();
            }, 1000);
        });

        console.log('[SessionSecurity] Monitoramento de inatividade (15 min) ativado.');
    }

    /**
     * Para o monitoramento de inatividade no logout
     */
    stopInactivityMonitoring() {
        this.isMonitoring = false;
        if (this.checkIntervalId) {
            clearInterval(this.checkIntervalId);
            this.checkIntervalId = null;
        }
        this.detachActivityListeners();
        this.ngZone.run(() => {
            this.isWarningModalVisible.set(false);
            this.remainingSeconds.set(60);
        });
        console.log('[SessionSecurity] Monitoramento de inatividade desativado.');
    }

    /**
     * Reseta manualmente o timer de inatividade (chamado ao clicar em "Continuar Trabalhando")
     */
    keepAlive() {
        this.recordUserActivity(true);
        this.ngZone.run(() => {
            this.isWarningModalVisible.set(false);
            this.remainingSeconds.set(60);
        });
        this.toast.show('Sessão revalidada com sucesso.', 'info');
    }

    /**
     * Força o encerramento imediato da sessão
     */
    forceLogout(reason: 'inactivity' | 'user' | 'tab_closed' = 'user') {
        const op = this.context.currentOperator();
        const role = this.context.currentUserRole();

        if (reason === 'inactivity') {
            this.audit.logAction(
                'SEGURANCA_SESSAO_EXPIRADA_INATIVIDADE',
                'Operador desconectado automaticamente por inatividade de 15 minutos',
                {
                    operatorId: op?.id,
                    operatorLogin: op?.login,
                    role: role,
                    timestamp: new Date().toISOString()
                },
                op?.name || 'SISTEMA'
            ).catch(err => console.error('[SessionSecurity] Erro ao registrar log de inatividade:', err));

            this.toast.show('Sua sessão foi encerrada por inatividade (15 min) para proteger os dados do paciente.', 'warning');
        }

        // Notifica outras abas para encerrarem também
        this.broadcastChannel?.postMessage({
            type: 'FORCE_LOGOUT',
            reason: reason,
            senderTabId: this.tabId
        });

        this.stopInactivityMonitoring();
        this.context.logout();
    }

    /**
     * Registra interação física do usuário (mouse, teclado, touch) com throttle
     */
    private recordUserActivity(force = false) {
        const now = Date.now();
        if (!force && (now - this.lastActivityRecorded < this.activityThrottleMs)) {
            return;
        }

        this.lastActivityRecorded = now;
        this.lastActivityTime = now;
        this.updateSharedActivityTime();

        // Se o modal de aviso estava aberto e o usuário voltou a interagir
        if (this.isWarningModalVisible()) {
            this.ngZone.run(() => {
                this.isWarningModalVisible.set(false);
                this.remainingSeconds.set(60);
            });
        }
    }

    /**
     * Verifica o tempo ocioso atual
     */
    private checkInactivityStatus() {
        if (!this.context.currentUserRole()) {
            this.stopInactivityMonitoring();
            return;
        }

        // Lê a última atividade compartilhada entre abas
        const sharedActivity = this.getSharedActivityTime();
        if (sharedActivity > this.lastActivityTime) {
            this.lastActivityTime = sharedActivity;
        }

        const idleTime = Date.now() - this.lastActivityTime;

        if (idleTime >= this.INACTIVITY_LIMIT_MS) {
            this.ngZone.run(() => {
                this.forceLogout('inactivity');
            });
        } else if (idleTime >= this.WARNING_THRESHOLD_MS) {
            const timeLeftSec = Math.max(0, Math.ceil((this.INACTIVITY_LIMIT_MS - idleTime) / 1000));
            this.ngZone.run(() => {
                this.isWarningModalVisible.set(true);
                this.remainingSeconds.set(timeLeftSec);
            });
        } else {
            if (this.isWarningModalVisible()) {
                this.ngZone.run(() => {
                    this.isWarningModalVisible.set(false);
                    this.remainingSeconds.set(60);
                });
            }
        }
    }

    // =========================================================================
    // CICLO DE VIDA DE ABAS & LOGOUT AUTOMÁTICO AO FECHAR
    // =========================================================================

    private initTabLifecycle() {
        if (typeof window === 'undefined') return;

        // Registra esta aba
        this.registerActiveTab();

        // Marca que está recarregando se for F5 para não deslogar inadvertidamente no refresh
        window.addEventListener('beforeunload', () => {
            sessionStorage.setItem('is_tab_reloading', 'true');
        });

        // Evento ao fechar aba ou descarregar página
        window.addEventListener('pagehide', (event) => {
            this.unregisterActiveTab();

            const isReloading = sessionStorage.getItem('is_tab_reloading') === 'true';

            // Se a aba está sendo fechada (não é reload) e não há outras abas ativas do sistema
            if (!isReloading && !event.persisted) {
                const remainingTabs = this.getActiveTabsCount();
                if (remainingTabs === 0 && this.context.currentUserRole()) {
                    console.log('[SessionSecurity] Última aba fechada. Encerrando sessão no Supabase.');
                    // Desconecta sessão do Supabase no fechamento
                    supabase.auth.signOut();
                }
            }
        });

        // Limpa flag de reload após carregar com sucesso
        window.addEventListener('load', () => {
            setTimeout(() => {
                sessionStorage.removeItem('is_tab_reloading');
            }, 1000);
        });
    }

    private registerActiveTab() {
        try {
            const tabsJson = localStorage.getItem('filasaude_active_tabs');
            const tabs: Record<string, number> = tabsJson ? JSON.parse(tabsJson) : {};
            tabs[this.tabId] = Date.now();
            localStorage.setItem('filasaude_active_tabs', JSON.stringify(tabs));
        } catch {
            // Ignore storage restrictions
        }
    }

    private unregisterActiveTab() {
        try {
            const tabsJson = localStorage.getItem('filasaude_active_tabs');
            if (tabsJson) {
                const tabs: Record<string, number> = JSON.parse(tabsJson);
                delete tabs[this.tabId];
                localStorage.setItem('filasaude_active_tabs', JSON.stringify(tabs));
            }
        } catch {
            // Ignore
        }
    }

    private getActiveTabsCount(): number {
        try {
            const tabsJson = localStorage.getItem('filasaude_active_tabs');
            if (!tabsJson) return 0;
            const tabs: Record<string, number> = JSON.parse(tabsJson);
            const now = Date.now();
            // Filtra abas que possam ter travado sem disparar pagehide (mais de 30s sem sinal)
            let count = 0;
            for (const id in tabs) {
                if (id !== this.tabId && (now - tabs[id] < 60000)) {
                    count++;
                }
            }
            return count;
        } catch {
            return 0;
        }
    }

    private initMultiTabChannel() {
        if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return;

        try {
            this.broadcastChannel = new BroadcastChannel('filasaude_session_security');
            this.broadcastChannel.onmessage = (event) => {
                if (event.data?.type === 'FORCE_LOGOUT') {
                    if (event.data?.senderTabId !== this.tabId) {
                        this.ngZone.run(() => {
                            this.stopInactivityMonitoring();
                            this.context.logout();
                            if (event.data.reason === 'inactivity') {
                                this.toast.show('Sessão encerrada em outra aba por inatividade.', 'warning');
                            }
                        });
                    }
                } else if (event.data?.type === 'USER_ACTIVITY') {
                    this.lastActivityTime = Math.max(this.lastActivityTime, event.data.timestamp);
                }
            };
        } catch (e) {
            console.warn('[SessionSecurity] BroadcastChannel não suportado neste navegador:', e);
        }
    }

    private updateSharedActivityTime() {
        const now = Date.now();
        try {
            localStorage.setItem('filasaude_last_activity', now.toString());
            this.broadcastChannel?.postMessage({
                type: 'USER_ACTIVITY',
                timestamp: now,
                senderTabId: this.tabId
            });
        } catch {
            // Ignore
        }
    }

    private getSharedActivityTime(): number {
        try {
            const val = localStorage.getItem('filasaude_last_activity');
            return val ? parseInt(val, 10) || 0 : 0;
        } catch {
            return 0;
        }
    }

    // =========================================================================
    // LISTENERS DE ATIVIDADE
    // =========================================================================

    private boundActivityHandler = () => this.recordUserActivity();

    private attachActivityListeners() {
        if (typeof window === 'undefined') return;
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        events.forEach(eventName => {
            window.addEventListener(eventName, this.boundActivityHandler, { passive: true });
        });
    }

    private detachActivityListeners() {
        if (typeof window === 'undefined') return;
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        events.forEach(eventName => {
            window.removeEventListener(eventName, this.boundActivityHandler);
        });
    }

    // =========================================================================
    // PROTEÇÃO CONTRA FORÇA BRUTA (BRUTE-FORCE LOCKOUT)
    // =========================================================================

    private checkInitialLockout() {
        try {
            const lockoutUntilStr = localStorage.getItem('filasaude_lockout_until');
            if (lockoutUntilStr) {
                const lockoutUntil = parseInt(lockoutUntilStr, 10);
                const remaining = lockoutUntil - Date.now();
                if (remaining > 0) {
                    this.triggerLockout(remaining);
                } else {
                    this.clearLockout();
                }
            }

            const attemptsStr = localStorage.getItem('filasaude_failed_attempts');
            if (attemptsStr) {
                this.failedAttempts.set(parseInt(attemptsStr, 10) || 0);
            }
        } catch {
            // Ignore
        }
    }

    canAttemptLogin(): { allowed: boolean; remainingSeconds?: number } {
        if (this.isLockedOut()) {
            return {
                allowed: false,
                remainingSeconds: this.lockoutRemainingSeconds()
            };
        }
        return { allowed: true };
    }

    recordFailedAttempt(loginInput: string): { isLocked: boolean; remainingSeconds?: number; attemptsLeft: number } {
        const current = this.failedAttempts() + 1;
        this.failedAttempts.set(current);
        try {
            localStorage.setItem('filasaude_failed_attempts', current.toString());
        } catch {
            // Ignore
        }

        // Registra evento de tentativa falha na auditoria
        this.audit.logAction(
            'SEGURANCA_LOGIN_FALHA',
            `Tentativa de login incorreta para o usuário: ${loginInput}`,
            {
                attemptNumber: current,
                maxAllowed: this.MAX_FAILED_ATTEMPTS,
                timestamp: new Date().toISOString()
            },
            loginInput || 'ANÔNIMO'
        ).catch(err => console.error('[SessionSecurity] Erro ao registrar auditoria de login falho:', err));

        if (current >= this.MAX_FAILED_ATTEMPTS) {
            this.triggerLockout(this.LOCKOUT_DURATION_MS);

            this.audit.logAction(
                'SEGURANCA_BLOQUEIO_FORCA_BRUTA',
                `Acesso temporariamente bloqueado por 5 minutos após ${this.MAX_FAILED_ATTEMPTS} tentativas inválidas para: ${loginInput}`,
                {
                    lockoutDurationMs: this.LOCKOUT_DURATION_MS,
                    timestamp: new Date().toISOString()
                },
                loginInput || 'ANÔNIMO'
            ).catch(err => console.error('[SessionSecurity] Erro ao registrar bloqueio de força bruta:', err));

            return {
                isLocked: true,
                remainingSeconds: Math.ceil(this.LOCKOUT_DURATION_MS / 1000),
                attemptsLeft: 0
            };
        }

        return {
            isLocked: false,
            attemptsLeft: this.MAX_FAILED_ATTEMPTS - current
        };
    }

    recordSuccessfulLogin() {
        this.clearLockout();
        this.startInactivityMonitoring();
    }

    private triggerLockout(durationMs: number) {
        const lockoutUntil = Date.now() + durationMs;
        try {
            localStorage.setItem('filasaude_lockout_until', lockoutUntil.toString());
        } catch {
            // Ignore
        }

        this.isLockedOut.set(true);
        this.lockoutRemainingSeconds.set(Math.ceil(durationMs / 1000));

        if (this.lockoutIntervalId) clearInterval(this.lockoutIntervalId);

        this.lockoutIntervalId = setInterval(() => {
            const remaining = lockoutUntil - Date.now();
            if (remaining <= 0) {
                this.clearLockout();
            } else {
                this.lockoutRemainingSeconds.set(Math.ceil(remaining / 1000));
            }
        }, 1000);
    }

    private clearLockout() {
        if (this.lockoutIntervalId) {
            clearInterval(this.lockoutIntervalId);
            this.lockoutIntervalId = null;
        }
        this.isLockedOut.set(false);
        this.lockoutRemainingSeconds.set(0);
        this.failedAttempts.set(0);
        try {
            localStorage.removeItem('filasaude_lockout_until');
            localStorage.removeItem('filasaude_failed_attempts');
        } catch {
            // Ignore
        }
    }
}
